import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { LoggingService } from './core/services/logging.service';
import { RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GlobalApplicationToolbarComponent } from './core/components/global-application-toolbar/global-application-toolbar.component';
import { SidebarComponent } from './core/components/sidebar/sidebar.component';
import { ToastComponent } from './core/components/toast/toast.component';
import { AuthenticationService } from './core/services/authentication.service';
import { NavigationTreeComponent } from './shared/components/navigation-tree/navigation-tree.component';
import { NavigationItem } from './shared/components/navigation-tree/models/navigation-item.model';
import { NAVIGATION_ITEMS } from './constants/navigation.constants';
import { RoutingLoaderService } from './services/routing-loader.service';
import { LoadingSpinnerComponent } from './components/loading-spinner/loading-spinner.component';
import { AppPermissionsService } from './services/app-permissions.service';
import { DocumentsService, FileStreamResult } from './openapi';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { getFilenameFromContentDisposition, saveBlob } from './helpers/file.helper';
import { HttpResponse } from '@angular/common/http';
import { CustomerSiteContextService } from './services/customer-site-context.service';

@Component({
  selector: 'adf-root',
  imports: [
    GlobalApplicationToolbarComponent,
    SidebarComponent,
    RouterModule,
    ToastComponent,
    NavigationTreeComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
})
export class AppComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly logger = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthenticationService);
  private readonly appPermissionsService = inject(AppPermissionsService);
  private readonly routingLoaderService = inject(RoutingLoaderService);
  private readonly documentsService = inject(DocumentsService);
  private readonly customerSiteContext = inject(CustomerSiteContextService);

  readonly isNavigating = this.routingLoaderService.isNavigating;

  sidebarIsOpen = signal(true);
  private readonly isNavigationReady = signal(false);
  private readonly hasNavigationFailed = signal(false);

  /** Stays collapsed when there is nothing to show: initialization failed or the user has no access. */
  readonly sidebarIsEnabled = computed(
    () => !this.hasNavigationFailed() && !(this.appPermissionsService.isLoaded() && !this.appPermissionsService.canRead())
  );

  /** Re-evaluated only when the user's access changes (signals), not on every change detection. */
  readonly navigationItems = computed<NavigationItem[]>(() =>
    this.isNavigationReady()
      ? NAVIGATION_ITEMS.filter((item) =>
          // Action items (e.g. terms of use download) call the API, which requires Read.
          item.path === null ? this.appPermissionsService.canRead() : this.appPermissionsService.hasAccessToRoute(item.path)
        )
      : []
  );

  constructor() {
    this.translateService.addLangs(['en']);
    this.translateService.setFallbackLang('en');
    this.translateService.use('en');
  }

  ngOnInit(): void {
    void this.initMsalAndLoadMainNavigation().catch((error: unknown) => {
      console.error('Failed to initialize app navigation.', error);
      this.isNavigationReady.set(false);
      this.hasNavigationFailed.set(true);
    });
  }

  private async initMsalAndLoadMainNavigation(): Promise<void> {
    await this.authService.initMsal();
    this.authService.checkAndSetActiveAccount();
    // Guarded navigation items appear once the user's access has loaded; users without Read get no
    // customer/site context, since every data endpoint requires Read.
    this.appPermissionsService
      .load()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.appPermissionsService.canRead()) {
          this.customerSiteContext.initialize();
        }
      });
    this.isNavigationReady.set(true);
  }

  protected onNavigationAction(itemId: string): void {
    if (itemId === 'terms-of-use') {
      this.downloadTermsOfUse();
    }
  }

  private downloadTermsOfUse(): void {
    const language = this.translateService.getCurrentLang();
    this.documentsService
      .apiDocumentsGetTermsOfUseGet(language, 'response', false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const blob = this.getBlobFromResponse(response, 'application/pdf');
          const filename = this.getFileNameFromResponse(response, language);

          if (blob) {
            saveBlob(blob, filename);
            this.logger.success(
              this.translateService.instant('COMMON.SUCCESS'),
              this.translateService.instant('TERMS_CONDITIONS.SIDEBAR.DOWNLOAD_SUCCESS')
            );
          } else {
            this.logger.error(
              this.translateService.instant('COMMON.ERROR'),
              this.translateService.instant('TERMS_CONDITIONS.SIDEBAR.DOWNLOAD_ERROR')
            );
          }
        },
        error: (err) => {
          const title = err?.error?.title || this.translateService.instant('COMMON.ERROR');
          this.logger.error(title, err);
        },
      });
  }

  private getBlobFromResponse(response: HttpResponse<FileStreamResult>, type: string): Blob | null {
    if (response.body?.fileStream) {
      return new Blob([response.body.fileStream], { type: type });
    } else if (response.body instanceof Blob) {
      return response.body;
    }
    return null;
  }

  private getFileNameFromResponse(response: HttpResponse<FileStreamResult>, language: string): string {
    if (response.body?.fileDownloadName) {
      return response.body.fileDownloadName;
    }
    const contentDisposition = response.headers.get('content-disposition');
    return (
      getFilenameFromContentDisposition(contentDisposition) ??
      this.translateService.instant('TERMS_CONDITIONS.SIDEBAR.DEFAULT_FILE_NAME', { lang: language })
    );
  }
}
